import { Component, inject, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import MTCButtonComponent, { type MTCButtonVariant } from '@mtc/mtc-button/mtc-button.component';
import MTCCustomIconComponent from '@mtc/mtc-custom-icon/mtc-custom-icon.component';

export type AlertType = 'error' | 'warning' | 'info' | 'success';

export interface AlertDialogData {
  type?: AlertType;
  title?: string;
  desktopTitle?: string;
  message: string;
  desktopMessage?: string;
  primaryAction: {
    label: string;
    handler?: () => Promise<void>;
    variant?: MTCButtonVariant;
  };
  secondaryAction?: {
    label: string;
    handler?: () => Promise<void>;
    variant?: MTCButtonVariant;
  };
  hideClose?: boolean;
  isMobile?: boolean;
  maxWidth?: string;
  disableClose?: boolean;
}

@Component({
  selector: 'app-alert-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MTCCustomIconComponent,
    MTCButtonComponent,
  ],
  templateUrl: './alert-dialog.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./alert-dialog.component.css'],
})
export class AlertDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AlertDialogComponent>);
  protected readonly data = inject<AlertDialogData>(MAT_DIALOG_DATA);

  protected isPrimaryProcessing = false;
  protected isSecondaryProcessing = false;

  async onPrimaryAction(): Promise<void> {
    if (this.isPrimaryProcessing) return;
    this.isPrimaryProcessing = true;
    setTimeout(async () => {
      try {
        if (this.data.primaryAction.handler) {
          await this.data.primaryAction.handler();
        }
        this.dialogRef.close(true);
      } finally {
        this.isPrimaryProcessing = false;
      }
    }, 300);
  }

  async onSecondaryAction(): Promise<void> {
    if (this.isSecondaryProcessing) return;
    this.isSecondaryProcessing = true;

    try {
      if (this.data.secondaryAction?.handler) {
        await this.data.secondaryAction.handler();
      }
      this.dialogRef.close(false);
    } finally {
      this.isSecondaryProcessing = false;
    }
  }
}
